'use client';

/**
 * DocumentTile - Displays knowledge base document search results
 *
 * Shows document information including:
 * - File name and type icon
 * - Repository name
 * - Preview text with highlights
 * - Chunk information (if applicable)
 * - File metadata (size, dates)
 */

import { FileText, Folder, Calendar, FileType, Hash } from 'lucide-react';
import { formatFileSize } from '@/lib/utils/format-bytes';
import { createTile, type BaseTileRenderProps } from './BaseTile';
import type { DocumentResult } from '../types';

/**
 * Get icon for file type
 */
function getFileTypeIcon(fileType?: string) {
  // Could expand this to show different icons per file type
  return FileText;
}

/**
 * Format date for display
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * DocumentTile component
 */
export const DocumentTile = createTile<DocumentResult>(
  'DocumentTile',
  (result) => {
    const { metadata } = result;
    const Icon = getFileTypeIcon(metadata.fileType);

    const renderProps: BaseTileRenderProps = {
      icon: Icon,
      iconColor: 'text-purple-400',
      title: result.title || metadata.fileName || 'Untitled Document',
      subtitle: metadata.repositoryName
        ? `${metadata.repositoryName}${metadata.path?.length ? ' / ' + metadata.path.join(' / ') : ''}`
        : undefined,
      preview: result.preview,
      metadata: [],
      tags: [],
    };

    // Add metadata items
    if (metadata.fileType) {
      renderProps.metadata!.push({
        label: 'Type',
        value: metadata.fileType.toUpperCase(),
        icon: FileType,
      });
    }

    if (metadata.fileSize) {
      renderProps.metadata!.push({
        label: 'Size',
        value: formatFileSize(metadata.fileSize, { mode: 'fixed-one', nullish: '' }),
      });
    }

    if (metadata.updatedAt) {
      renderProps.metadata!.push({
        label: 'Updated',
        value: formatDate(metadata.updatedAt),
        icon: Calendar,
      });
    }

    // Show chunk info if this is a chunk result
    if (metadata.chunkIndex !== undefined && metadata.totalChunks !== undefined) {
      renderProps.metadata!.push({
        label: 'Chunk',
        value: `${metadata.chunkIndex + 1} of ${metadata.totalChunks}`,
        icon: Hash,
      });
    }

    return renderProps;
  }
);

export default DocumentTile;
